/**
 * src/features/billing/lib/useUpgradeModal.ts
 *
 * Shared non-persisted Zustand store for the UpgradePromptModal.
 * Pattern mirrors useSyncStore: no persist() wrapper, ephemeral state only.
 *
 * State:
 *   open          — whether the modal is visible
 *   feature       — which Pro feature triggered the prompt ('sync' | 'analytics')
 *   loading       — true while the checkout session is being created
 *   error         — non-null when the checkout POST failed
 *
 * Actions:
 *   openUpgrade(feature) — opens the modal for the given feature
 *   close()              — closes the modal, clears error
 *   handleCheckout()     — POSTs /api/billing/checkout, redirects on success,
 *                          sets error flag on failure
 *
 * Usage from non-React code (e.g. useSyncStore.flush):
 *   useUpgradeModal.getState().openUpgrade('sync')
 *
 * T-08-19: dismissing never grants sync access — server 402 remains
 * authoritative on every retry.
 */

import { create } from 'zustand';

export type UpgradeFeature = 'sync' | 'analytics';

interface UpgradeModalState {
  open: boolean;
  feature: UpgradeFeature;
  loading: boolean;
  error: string | null;

  openUpgrade: (feature: UpgradeFeature) => void;
  close: () => void;
  handleCheckout: (interval?: 'monthly' | 'annual') => Promise<void>;
}

export const useUpgradeModal = create<UpgradeModalState>()((set) => ({
  open: false,
  feature: 'sync',
  loading: false,
  error: null,

  openUpgrade: (feature: UpgradeFeature) => set({ open: true, feature, error: null }),

  close: () => set({ open: false, error: null }),

  handleCheckout: async (interval: 'monthly' | 'annual' = 'monthly') => {
    set({ loading: true, error: null });
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interval }),
      });
      if (!res.ok) {
        set({ error: 'checkout_error' });
        return;
      }
      const { url } = (await res.json()) as { url: string };
      window.location.href = url;
    } catch {
      set({ error: 'checkout_error' });
    } finally {
      set({ loading: false });
    }
  },
}));
